import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import { Beaker, Search, Calendar as CalendarIcon, Loader2, ExternalLink, Upload, Truck, XCircle, CheckCircle2, Lock, Paperclip } from "lucide-react";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle, DialogDescription } from "../components/ui/dialog";
import { toast } from "sonner";
import { useAuth } from "../App";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Same defensive date normalizer used in LeadDetail — covers legacy Excel
// serial numbers in sample_date until the backfill endpoint is run.
const formatSampleDate = (raw) => {
  if (raw === null || raw === undefined) return '—';
  const s = String(raw).trim();
  if (!s) return '—';
  if (/^\d{4}-\d{2}-\d{2}/.test(s)) return s.slice(0, 10);
  const num = Number(s);
  if (Number.isFinite(num) && num >= 20000 && num <= 80000) {
    const ms = (num * 86400 * 1000) + Date.UTC(1899, 11, 30);
    const d = new Date(ms);
    if (!isNaN(d.getTime())) return d.toISOString().slice(0, 10);
  }
  const m = s.match(/^(\d{1,2})[\/\-](\d{1,2})[\/\-](\d{4})$/);
  if (m) {
    const [, a, b, y] = m;
    return `${y}-${b.padStart(2,'0')}-${a.padStart(2,'0')}`;
  }
  const parsed = new Date(s);
  if (!isNaN(parsed.getTime())) return parsed.toISOString().slice(0, 10);
  return s;
};

export default function Samples() {
  const { user } = useAuth();
  const isLogisticsRole = user?.role === 'logistics';
  const canEditLogistics = isLogisticsRole || ['admin', 'super_admin'].includes(user?.role);
  const [samples, setSamples] = useState([]);
  const [search, setSearch] = useState("");
  const [days, setDays] = useState("all");
  const [agents, setAgents] = useState([]);
  const [agentId, setAgentId] = useState("all");
  const [fromDate, setFromDate] = useState("");
  const [toDate, setToDate] = useState("");
  const [logisticsSample, setLogisticsSample] = useState(null);  // sample currently open in the LogisticsModal
  const [notDispatchedOnly, setNotDispatchedOnly] = useState(false);
  const [isLoading, setIsLoading] = useState(true);

  const fetchSamples = async () => {
    try {
      setIsLoading(true);
      const params = new URLSearchParams();
      if (search.trim()) params.append("search", search.trim());
      if (days !== "all") params.append("days", days);
      if (agentId !== "all") params.append("agent_id", agentId);
      if (fromDate) params.append("from_date", fromDate);
      if (toDate) params.append("to_date", toDate);
      if (notDispatchedOnly) params.append("not_dispatched", "true");
      const r = await axios.get(`${API}/samples?${params.toString()}`);
      setSamples(r.data?.samples || []);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to load samples");
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    (async () => {
      try {
        const r = await axios.get(`${API}/lead-management/agents`);
        setAgents(r.data?.agents || []);
      } catch (_e) {/* not all roles have access */}
    })();
  }, []);

  useEffect(() => {
    fetchSamples();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [days, agentId, fromDate, toDate, notDispatchedOnly]);

  const notDispatchedCount = samples.filter((s) => !s.logistics_status).length;

  return (
    <div className="p-6 space-y-6" data-testid="samples-page">
      <div className="flex items-start justify-between">
        <div>
          <h1 className="text-2xl font-bold text-slate-900 flex items-center gap-2">
            <Beaker className="w-6 h-6 text-blue-600" /> Samples
          </h1>
          <p className="text-slate-500 mt-1 text-sm">Every sample pushed into the CRM — across leads, agents and dates.</p>
        </div>
      </div>

      <Card className="border-slate-200">
        <CardHeader className="pb-3">
          <CardTitle className="text-base font-semibold">Filters</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 md:grid-cols-5 gap-3">
            <div className="md:col-span-2 relative">
              <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
              <Input
                placeholder="Search company, product, LFB ID, phone, GST…"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                onKeyDown={(e) => e.key === "Enter" && fetchSamples()}
                className="pl-9"
                data-testid="samples-search"
              />
            </div>
            <Select value={days} onValueChange={setDays}>
              <SelectTrigger data-testid="samples-days"><SelectValue placeholder="Time range" /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All time</SelectItem>
                <SelectItem value="7">Last 7 days</SelectItem>
                <SelectItem value="14">Last 14 days</SelectItem>
                <SelectItem value="30">Last 30 days</SelectItem>
                <SelectItem value="90">Last 90 days</SelectItem>
              </SelectContent>
            </Select>
            {agents.length > 0 && (
              <Select value={agentId} onValueChange={setAgentId}>
                <SelectTrigger data-testid="samples-agent"><SelectValue placeholder="Agent" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All agents</SelectItem>
                  {agents.map((a) => (
                    <SelectItem key={a.id} value={a.id}>{a.name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            )}
            <div className="flex gap-2">
              <Input type="date" value={fromDate} onChange={(e) => setFromDate(e.target.value)} placeholder="From" />
              <Input type="date" value={toDate} onChange={(e) => setToDate(e.target.value)} placeholder="To" />
            </div>
          </div>
          <div className="mt-3 flex flex-wrap gap-2 justify-between items-center">
            <button
              type="button"
              onClick={() => setNotDispatchedOnly((v) => !v)}
              className={`inline-flex items-center gap-2 px-3 py-1.5 text-xs font-medium rounded-full border transition-colors ${
                notDispatchedOnly
                  ? 'bg-amber-500 text-white border-amber-500 shadow-sm hover:bg-amber-600'
                  : 'bg-white text-amber-700 border-amber-300 hover:bg-amber-50'
              }`}
              data-testid="samples-not-dispatched-toggle"
              title="Show only samples that haven't been dispatched yet"
            >
              <Truck className="w-3.5 h-3.5" />
              Not Dispatched
              {notDispatchedOnly && notDispatchedCount > 0 && (
                <span className="ml-1 px-1.5 py-0.5 text-[10px] font-semibold bg-white text-amber-700 rounded-full">
                  {notDispatchedCount}
                </span>
              )}
            </button>
            <div className="flex gap-2">
              <Button variant="outline" onClick={() => { setSearch(""); setDays("all"); setAgentId("all"); setFromDate(""); setToDate(""); setNotDispatchedOnly(false); }}>Clear</Button>
              <Button onClick={fetchSamples}><Search className="w-4 h-4 mr-2" />Search</Button>
            </div>
          </div>
        </CardContent>
      </Card>

      <Card className="border-slate-200">
        <CardHeader className="pb-3 flex flex-row items-center justify-between">
          <CardTitle className="text-base font-semibold">
            {samples.length} sample{samples.length === 1 ? "" : "s"}
          </CardTitle>
        </CardHeader>
        <CardContent>
          {isLoading ? (
            <div className="py-12 text-center text-slate-400">
              <Loader2 className="w-6 h-6 mx-auto animate-spin" />
            </div>
          ) : samples.length === 0 ? (
            <div className="py-12 text-center text-slate-400">No samples found for the selected filters.</div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead className="bg-slate-50 border-y border-slate-200">
                  <tr>
                    <th className="text-left py-2 px-3 text-xs font-semibold text-slate-600 uppercase tracking-wide">Sample Requested</th>
                    <th className="text-left py-2 px-3 text-xs font-semibold text-slate-600 uppercase tracking-wide">Sample Dispatched</th>
                    <th className="text-left py-2 px-3 text-xs font-semibold text-slate-600 uppercase tracking-wide">Sample Delivered</th>
                    <th className="text-left py-2 px-3 text-xs font-semibold text-slate-600 uppercase tracking-wide">Company</th>
                    <th className="text-left py-2 px-3 text-xs font-semibold text-slate-600 uppercase tracking-wide">Product Name</th>
                    <th className="text-left py-2 px-3 text-xs font-semibold text-slate-600 uppercase tracking-wide">LFB ID</th>
                    <th className="text-left py-2 px-3 text-xs font-semibold text-slate-600 uppercase tracking-wide">Lead Owner</th>
                    <th className="text-left py-2 px-3 text-xs font-semibold text-slate-600 uppercase tracking-wide">Logistics Status</th>
                    <th className="text-left py-2 px-3 text-xs font-semibold text-slate-600 uppercase tracking-wide">AWB</th>
                    {canEditLogistics && (
                      <th className="text-left py-2 px-3 text-xs font-semibold text-slate-600 uppercase tracking-wide">Action</th>
                    )}
                    {!isLogisticsRole && (
                      <th className="text-left py-2 px-3 text-xs font-semibold text-slate-600 uppercase tracking-wide w-12"></th>
                    )}
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-200">
                  {samples.map((s) => (
                    <tr key={s.sample_id} className="hover:bg-slate-50" data-testid={`sample-row-${s.sample_id}`}>
                      <td className="py-2 px-3 text-slate-700">{formatSampleDate(s.sample_date)}</td>
                      <td className="py-2 px-3 text-slate-700">{s.handover_date || "—"}</td>
                      <td className="py-2 px-3 text-slate-700">{s.delivered_date || "—"}</td>
                      <td className="py-2 px-3 font-medium text-slate-900">{s.company_name || "—"}</td>
                      <td className="py-2 px-3 text-slate-700">{s.product_name}</td>
                      <td className="py-2 px-3 font-mono text-xs text-blue-600">{s.lfb_id || "—"}</td>
                      <td className="py-2 px-3 text-slate-700">{s.agent_name || "—"}</td>
                      <td className="py-2 px-3">
                        {s.logistics_status === 'dispatched' && (
                          <span className="inline-flex items-center gap-1 text-xs px-2 py-0.5 rounded bg-green-50 text-green-700 border border-green-200">
                            <Truck className="w-3 h-3" /> Dispatched
                            {s.logistics_locked && <Lock className="w-3 h-3 ml-0.5" title="Locked (>24h)" />}
                          </span>
                        )}
                        {s.logistics_status === 'delivered' && (
                          <span
                            className="inline-flex items-center gap-1 text-xs px-2 py-0.5 rounded bg-emerald-50 text-emerald-700 border border-emerald-200"
                            title={s.delivered_by_name ? `Delivered${s.delivered_date ? ' on ' + s.delivered_date : ''} · ${s.delivered_by_name}` : 'Delivered'}
                          >
                            <CheckCircle2 className="w-3 h-3" /> Delivered
                          </span>
                        )}
                        {s.logistics_status === 'rejected' && (
                          <span className="inline-flex items-center gap-1 text-xs px-2 py-0.5 rounded bg-red-50 text-red-700 border border-red-200" title={s.rejection_reason}>
                            <XCircle className="w-3 h-3" /> Rejected
                          </span>
                        )}
                        {!s.logistics_status && (
                          <span className="inline-flex items-center gap-1 text-xs px-2 py-0.5 rounded bg-amber-50 text-amber-700 border border-amber-200">
                            Not Dispatched
                          </span>
                        )}
                      </td>
                      <td className="py-2 px-3 text-xs">
                        {s.awb_number ? (
                          <div className="flex items-center gap-1">
                            <span className="font-mono">{s.awb_number}</span>
                            {s.awb_document?.download_url && (
                              <a
                                href={`${API}${s.awb_document.download_url}?auth=${localStorage.getItem('crm_token') || ''}`}
                                target="_blank"
                                rel="noopener noreferrer"
                                className="text-blue-600 hover:text-blue-800"
                                title="Open AWB document"
                                data-testid={`awb-link-${s.sample_id}`}
                              >
                                <Paperclip className="w-3.5 h-3.5" />
                              </a>
                            )}
                          </div>
                        ) : (
                          <span className="text-slate-400">—</span>
                        )}
                      </td>
                      {canEditLogistics && (
                        <td className="py-2 px-3">
                          <Button
                            size="sm"
                            variant="outline"
                            className="h-7 text-xs"
                            onClick={() => setLogisticsSample(s)}
                            disabled={isLogisticsRole && s.logistics_locked}
                            data-testid={`update-logistics-btn-${s.sample_id}`}
                          >
                            {s.logistics_status ? 'Edit' : 'Update'}
                          </Button>
                        </td>
                      )}
                      {!isLogisticsRole && (
                        <td className="py-2 px-3">
                          <Link to={`/leads/${s.lead_id}`} className="text-blue-600 hover:underline" title="Open lead">
                            <ExternalLink className="w-4 h-4" />
                          </Link>
                        </td>
                      )}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </CardContent>
      </Card>

      <LogisticsModal
        sample={logisticsSample}
        onClose={() => setLogisticsSample(null)}
        onSaved={() => { setLogisticsSample(null); fetchSamples(); }}
      />
    </div>
  );
}


// -----------------------------------------------------------------------------
// LogisticsModal — dispatched/rejected form used by logistics + admin users.
// - Dispatched: AWB number + AWB document upload (any file up to 10 MB).
// - Rejected: reason (enum) + optional photos (up to 5) or notes when "Others".
// - 24h edit lock is enforced by the backend for logistics role; UI mirrors it.
// -----------------------------------------------------------------------------
const REJECTION_REASONS = [
  "GSM didn't match as per client requirement",
  "Client composition didn't match",
  "Client wanted more quantity, sample is less",
  "Others",
];

function LogisticsModal({ sample, onClose, onSaved }) {
  const open = !!sample;
  // Whether this sample has already been dispatched — Delivered can only be
  // marked on top of a dispatched sample (per product decision 1a).
  const alreadyDispatched = sample?.logistics_status === 'dispatched' || sample?.logistics_status === 'delivered';
  const [handoverDate, setHandoverDate] = useState("");
  const [status, setStatus] = useState("dispatched");
  const [awbNumber, setAwbNumber] = useState("");
  const [awbDocument, setAwbDocument] = useState(null);
  const [rejectionReason, setRejectionReason] = useState("");
  const [rejectionNotes, setRejectionNotes] = useState("");
  const [rejectionPhotos, setRejectionPhotos] = useState([]);
  const [deliveredDate, setDeliveredDate] = useState("");
  const [awbDeliveryDocument, setAwbDeliveryDocument] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!sample) return;
    setHandoverDate(sample.handover_date || new Date().toISOString().slice(0, 10));
    setStatus(sample.logistics_status || "dispatched");
    setAwbNumber(sample.awb_number || "");
    setAwbDocument(sample.awb_document || null);
    setRejectionReason(sample.rejection_reason || "");
    setRejectionNotes(sample.rejection_notes || "");
    setRejectionPhotos(Array.isArray(sample.rejection_photos) ? sample.rejection_photos : []);
    setDeliveredDate(sample.delivered_date || new Date().toISOString().slice(0, 10));
    setAwbDeliveryDocument(sample.awb_delivery_document || null);
  }, [sample]);

  const uploadFile = async (file, purpose) => {
    const form = new FormData();
    form.append("file", file);
    const r = await axios.post(`${API}/uploads?purpose=${purpose}`, form);
    return r.data;
  };

  const onAwbFileChange = async (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    setUploading(true);
    try {
      const doc = await uploadFile(f, "awb_document");
      setAwbDocument(doc);
    } catch (err) {
      toast.error(err.response?.data?.detail || "AWB upload failed");
    } finally {
      setUploading(false);
      e.target.value = "";
    }
  };

  const onRejectionPhotoAdd = async (e) => {
    const files = Array.from(e.target.files || []);
    if (!files.length) return;
    const remaining = 5 - rejectionPhotos.length;
    if (remaining <= 0) {
      toast.error("Max 5 photos");
      return;
    }
    setUploading(true);
    try {
      const uploaded = [];
      for (const f of files.slice(0, remaining)) {
        const doc = await uploadFile(f, "rejection_photo");
        uploaded.push(doc);
      }
      setRejectionPhotos([...rejectionPhotos, ...uploaded]);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Photo upload failed");
    } finally {
      setUploading(false);
      e.target.value = "";
    }
  };

  const onDeliveryFileChange = async (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    setUploading(true);
    try {
      const doc = await uploadFile(f, "awb_document");
      setAwbDeliveryDocument(doc);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Delivery screenshot upload failed");
    } finally {
      setUploading(false);
      e.target.value = "";
    }
  };

  const removePhoto = (i) => setRejectionPhotos(rejectionPhotos.filter((_, idx) => idx !== i));

  const handleSave = async () => {
    if (!handoverDate) { toast.error("Handover date is required"); return; }
    if (status === "dispatched") {
      if (!awbNumber.trim()) { toast.error("AWB number is required"); return; }
      if (!awbDocument) { toast.error("Upload the AWB document"); return; }
    } else if (status === "delivered") {
      if (!alreadyDispatched) { toast.error("Sample must be dispatched first before it can be delivered"); return; }
      if (!awbDeliveryDocument) { toast.error("Upload the AWB delivery screenshot"); return; }
    } else {
      if (!rejectionReason) { toast.error("Pick a rejection reason"); return; }
      if (rejectionReason === "Others" && !rejectionNotes.trim()) { toast.error("Notes are required for 'Others'"); return; }
    }
    setSaving(true);
    try {
      await axios.patch(`${API}/samples/${sample.sample_id}/logistics`, {
        handover_date: handoverDate,
        status,
        awb_number: status === "dispatched" ? awbNumber.trim() : undefined,
        awb_document: status === "dispatched" ? awbDocument : undefined,
        rejection_reason: status === "rejected" ? rejectionReason : undefined,
        rejection_notes: status === "rejected" ? rejectionNotes.trim() : undefined,
        rejection_photos: status === "rejected" ? rejectionPhotos : undefined,
        delivered_date: status === "delivered" ? deliveredDate : undefined,
        awb_delivery_document: status === "delivered" ? awbDeliveryDocument : undefined,
      });
      toast.success(
        status === "dispatched" ? "Dispatch logged & AM notified"
          : status === "delivered" ? "Delivery recorded"
          : "Rejection logged"
      );
      onSaved && onSaved();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Save failed");
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="max-w-lg" data-testid="logistics-modal">
        <DialogHeader>
          <DialogTitle>Update logistics — {sample?.company_name || "Sample"}</DialogTitle>
          <DialogDescription>
            {sample?.product_name || ""} · LFB {sample?.lfb_id || "—"}
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-4 py-2">
          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-1.5">
              <Label>Handover date *</Label>
              <Input
                type="date"
                value={handoverDate}
                onChange={(e) => setHandoverDate(e.target.value)}
                data-testid="logistics-handover-date"
              />
            </div>
            <div className="space-y-1.5">
              <Label>Status *</Label>
              <Select value={status} onValueChange={setStatus}>
                <SelectTrigger data-testid="logistics-status"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="dispatched">Sample dispatched</SelectItem>
                  {/* Delivered is only markable after a sample has been dispatched. */}
                  {alreadyDispatched && (
                    <SelectItem value="delivered" data-testid="logistics-status-delivered">Sample delivered</SelectItem>
                  )}
                  <SelectItem value="rejected">Sample rejected</SelectItem>
                </SelectContent>
              </Select>
              {!alreadyDispatched && (
                <p className="text-[11px] text-slate-400">Mark as dispatched first — "Delivered" unlocks once AWB is in flight.</p>
              )}
            </div>
          </div>

          {status === "dispatched" && (
            <>
              <div className="space-y-1.5">
                <Label>AWB number *</Label>
                <Input
                  value={awbNumber}
                  onChange={(e) => setAwbNumber(e.target.value)}
                  placeholder="e.g. 1234567890"
                  data-testid="logistics-awb-number"
                />
              </div>
              <div className="space-y-1.5">
                <Label>AWB document * (PDF/JPG/PNG/DOCX, max 10 MB)</Label>
                <input
                  type="file"
                  accept=".pdf,image/*,.doc,.docx"
                  onChange={onAwbFileChange}
                  disabled={uploading}
                  data-testid="logistics-awb-file"
                />
                {awbDocument && (
                  <div className="text-xs text-slate-600 flex items-center gap-1 mt-1">
                    <Paperclip className="w-3 h-3" /> {awbDocument.original_filename}
                  </div>
                )}
              </div>
            </>
          )}

          {status === "delivered" && (
            <>
              <div className="space-y-1.5">
                <Label>Delivery date *</Label>
                <Input
                  type="date"
                  value={deliveredDate}
                  onChange={(e) => setDeliveredDate(e.target.value)}
                  data-testid="logistics-delivered-date"
                />
              </div>
              <div className="space-y-1.5">
                <Label>AWB delivery screenshot * (PDF/JPG/PNG/DOCX, max 10 MB)</Label>
                <input
                  type="file"
                  accept=".pdf,image/*,.doc,.docx"
                  onChange={onDeliveryFileChange}
                  disabled={uploading}
                  data-testid="logistics-delivery-file"
                />
                {awbDeliveryDocument && (
                  <div className="text-xs text-slate-600 flex items-center gap-1 mt-1">
                    <Paperclip className="w-3 h-3" /> {awbDeliveryDocument.original_filename}
                  </div>
                )}
                <p className="text-[11px] text-slate-400">
                  Attach the courier's proof-of-delivery. Recorded under your account.
                </p>
              </div>
            </>
          )}

          {status === "rejected" && (
            <>
              <div className="space-y-1.5">
                <Label>Rejection reason *</Label>
                <Select value={rejectionReason} onValueChange={setRejectionReason}>
                  <SelectTrigger data-testid="logistics-rejection-reason"><SelectValue placeholder="Pick a reason" /></SelectTrigger>
                  <SelectContent>
                    {REJECTION_REASONS.map(r => <SelectItem key={r} value={r}>{r}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              {(rejectionReason === "Others" || rejectionNotes) && (
                <div className="space-y-1.5">
                  <Label>{rejectionReason === "Others" ? "Notes *" : "Additional notes"}</Label>
                  <Textarea
                    value={rejectionNotes}
                    onChange={(e) => setRejectionNotes(e.target.value)}
                    rows={3}
                    data-testid="logistics-rejection-notes"
                  />
                </div>
              )}
              <div className="space-y-1.5">
                <Label>Photos (optional, up to 5)</Label>
                <input
                  type="file"
                  accept="image/*"
                  multiple
                  onChange={onRejectionPhotoAdd}
                  disabled={uploading || rejectionPhotos.length >= 5}
                  data-testid="logistics-rejection-photos"
                />
                {rejectionPhotos.length > 0 && (
                  <div className="flex flex-wrap gap-2 mt-1">
                    {rejectionPhotos.map((p, i) => (
                      <div key={p.file_id} className="text-xs bg-slate-100 rounded px-2 py-1 flex items-center gap-1">
                        <Paperclip className="w-3 h-3" />
                        {p.original_filename}
                        <button onClick={() => removePhoto(i)} className="text-red-500 ml-1" title="Remove">×</button>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </>
          )}
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose} disabled={saving}>Cancel</Button>
          <Button onClick={handleSave} disabled={saving || uploading} data-testid="logistics-save-btn">
            {saving ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : (
              status === "dispatched" ? <Truck className="w-4 h-4 mr-2" />
                : status === "delivered" ? <CheckCircle2 className="w-4 h-4 mr-2" />
                : <XCircle className="w-4 h-4 mr-2" />
            )}
            {saving ? "Saving..." : "Save"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

